import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from './components/Toast';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import OnSpotAccessGate from './components/auth/OnSpotAccessGate';
import DeskAccessGate from './components/auth/DeskAccessGate';
import ErrorBoundary from './components/ErrorBoundary';
import AuthPage from './pages/AuthPage';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import BookDemoPage from './pages/BookDemoPage';
import CheckInMethodPage from './pages/CheckInMethodPage';
import EventRegistration from './pages/EventRegistration';
import AdminDashboard from './pages/AdminDashboard';
import EventManagement from './pages/EventManagement';
import EventRegistrations from './pages/EventRegistrations';
import EventLockers from './pages/EventLockers';
import EventAnalytics from './pages/EventAnalytics';
import SharedStatsPage from './pages/SharedStatsPage';
import MyRegistrationPage from './pages/MyRegistrationPage';
import MyRegistrationLookupPage from './pages/MyRegistrationLookupPage';
import TestimonialSubmitPage from './pages/TestimonialSubmitPage';
import PublicEventsPage from './pages/PublicEventsPage';
import GalleryPage from './pages/GalleryPage';
import WebsiteContentPage from './pages/WebsiteContentPage';
import ViewerDesk from './pages/ViewerDesk';
import QRScanFlowPage from './pages/QRScanFlowPage';
import TeamManagement from './pages/TeamManagement';
import FormBuilderSettings from './pages/FormBuilderSettings';
import BadgeConfigurationPage from './pages/BadgeConfigurationPage';
import BadgeDesignerPage from './pages/BadgeDesignerPage';
import EmailTemplatesSettings from './pages/EmailTemplatesSettings';
import CouponsPage from './pages/CouponsPage';
import CouponDetailPage from './pages/CouponDetailPage';
import EventPaymentSettings from './pages/EventPaymentSettings';
import PaymentMerchantsPage from './pages/PaymentMerchantsPage';
import QrCodeGeneratorPage from './pages/QrCodeGeneratorPage';
import LanServerPage from './pages/LanServerPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AccountSettings from './pages/AccountSettings';
import AboutUsPage from './pages/AboutUsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import ShippingPolicyPage from './pages/ShippingPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import ContactUsPage from './pages/ContactUsPage';
import TeamPortalPage from './pages/TeamPortalPage';
import { EventRefGate } from './utils/eventRef';
import RouteManifest from './components/RouteManifest';
import EventSettingsPage from './pages/EventSettingsPage';
import AccessCodesPage from './pages/AccessCodesPage';
import { OnSpotEntry, RegisterEntry, TicketingPage } from './pages/ticketing/TicketingRoutes';
import BookingStatusPage from './pages/ticketing/BookingStatusPage';
import TicketingSettingsPage from './pages/TicketingSettingsPage';
import { DomainHome, DomainLogin, DomainScope } from './components/auth/CustomDomainRoutes';
import './index.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  useEffect(() => {
    const blockNumberArrows = (event) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      const el = event.target;
      if (!(el instanceof HTMLInputElement) || el.type !== 'number') return;
      if (el.closest('[data-allow-number-keys]')) return;
      event.preventDefault();
    };
    document.addEventListener('keydown', blockNumberArrows);
    return () => document.removeEventListener('keydown', blockNumberArrows);
  }, []);
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <Router>
              <div className="min-h-screen bg-background">
                <RouteManifest />
                <DomainScope>
                <Routes>
                {/* Public Routes */}
                <Route path="/" element={<DomainHome><HomePage /></DomainHome>} />
                <Route path="/login" element={<DomainLogin />} />
                <Route path="/team/:eventId" element={<TeamPortalPage />} />
                <Route path="/register/:eventId" element={<EventRefGate>
                  <RegisterEntry legacy={(
                    <OnSpotAccessGate>
                      <EventRegistration />
                    </OnSpotAccessGate>
                  )} />
                </EventRefGate>} />
                <Route path="/register/:eventId/passes/:groupId" element={<EventRefGate><TicketingPage kind="passes" /></EventRefGate>} />
                <Route path="/register/:eventId/book/:passId" element={<EventRefGate><TicketingPage kind="book" /></EventRefGate>} />
                <Route path="/register/:eventId/booking/:bookingId" element={<EventRefGate><BookingStatusPage /></EventRefGate>} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/stats/:token" element={<SharedStatsPage />} />
                <Route path="/my-registration" element={<MyRegistrationLookupPage />} />
                <Route path="/testimonial" element={<TestimonialSubmitPage />} />
                <Route path="/demo" element={<BookDemoPage />} />
                <Route path="/check-in" element={<Navigate to="/#check-in" replace />} />
                <Route path="/check-in/:method" element={<CheckInMethodPage />} />
                <Route path="/events" element={<PublicEventsPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/gallery/:albumId" element={<GalleryPage />} />
                <Route path="/my-registration/:token" element={<MyRegistrationPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                
                {/* Compliance Pages */}
                <Route path="/about-us" element={<AboutUsPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                <Route path="/refund-policy" element={<RefundPolicyPage />} />
                <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
                <Route path="/contact-us" element={<ContactUsPage />} />
                
                {/* On-Spot Registration Route - access is configured per event */}
                <Route path="/register/:eventId/on-spot" element={<EventRefGate>
                  <OnSpotEntry />
                </EventRefGate>} />
                
                {/* Viewer desk - logged-in staff landing page */}
                <Route path="/desk" element={
                  <ProtectedRoute requiredRole="VIEWER">
                    <ViewerDesk />
                  </ProtectedRoute>
                } />
                <Route path="/desk/:eventId" element={<EventRefGate>
                  <DeskAccessGate>
                    <ViewerDesk />
                  </DeskAccessGate>
                </EventRefGate>} />

                {/* Public Scan Route - no auth required */}
                <Route path="/admin/events/:eventId/scan" element={<EventRefGate><QRScanFlowPage /></EventRefGate>} />
                
                {/* Admin Routes - Protected (requires auth) */}
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="account" element={<ProtectedRoute requiredRole="ADMIN"><AccountSettings /></ProtectedRoute>} />
                  <Route path="lan" element={
                    <ProtectedRoute requiredRole="ADMIN">
                      <LanServerPage />
                    </ProtectedRoute>
                  } />
                  <Route path="qr-generator" element={
                    <ProtectedRoute requiredRole="EDITOR">
                      <QrCodeGeneratorPage />
                    </ProtectedRoute>
                  } />
                  <Route path="events" element={
                    <ProtectedRoute requiredRole="EDITOR">
                      <EventManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="events/:eventId/registrations" element={<EventRefGate>
                    <ProtectedRoute requiredRole="VIEWER">
                      <EventRegistrations />
                    </ProtectedRoute>
                  </EventRefGate>} />
                  <Route path="events/:eventId/analytics" element={<EventRefGate>
                    <ProtectedRoute requiredRole="VIEWER">
                      <EventAnalytics />
                    </ProtectedRoute>
                  </EventRefGate>} />
                  <Route path="events/:eventId/lockers" element={<EventRefGate>
                    <ProtectedRoute requiredRole="VIEWER">
                      <EventLockers />
                    </ProtectedRoute>
                  </EventRefGate>} />
                  <Route path="events/:eventId/team" element={<EventRefGate>
                    <ProtectedRoute requiredRole="ADMIN">
                      <TeamManagement />
                    </ProtectedRoute>
                  </EventRefGate>} />
                  <Route path="events/:eventId/access-codes" element={<EventRefGate>
                    <ProtectedRoute requiredRole="EDITOR">
                      <AccessCodesPage />
                    </ProtectedRoute>
                  </EventRefGate>} />
                  <Route path="events/:eventId/settings" element={<EventRefGate>
                    <ProtectedRoute requiredRole="ADMIN">
                      <EventSettingsPage />
                    </ProtectedRoute>
                  </EventRefGate>} />
                  <Route path="events/:eventId/premium" element={<EventRefGate>
                    <ProtectedRoute requiredRole="ADMIN">
                      <TicketingSettingsPage />
                    </ProtectedRoute>
                  </EventRefGate>} />
                  <Route path="events/:eventId/payment-settings" element={<EventRefGate>
                    <ProtectedRoute requiredRole="EDITOR">
                      <EventPaymentSettings />
                    </ProtectedRoute>
                  </EventRefGate>} />
                  <Route path="website" element={
                    <ProtectedRoute requiredRole="ADMIN">
                      <WebsiteContentPage />
                    </ProtectedRoute>
                  } />
                  <Route path="organizers" element={
                    <ProtectedRoute requiredRole="ADMIN">
                      <PaymentMerchantsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="events/:eventId/badges" element={<EventRefGate>
                    <ProtectedRoute requiredRole="EDITOR">
                      <BadgeConfigurationPage />
                    </ProtectedRoute>
                  </EventRefGate>} />
                  <Route path="events/:eventId/badge-config" element={<EventRefGate>
                    <ProtectedRoute requiredRole="EDITOR">
                      <BadgeDesignerPage />
                    </ProtectedRoute>
                  </EventRefGate>} />
                  <Route path="settings/forms" element={
                    <ProtectedRoute requiredRole="ADMIN">
                      <FormBuilderSettings />
                    </ProtectedRoute>
                  } />
                  <Route path="settings/email-templates" element={
                    <ProtectedRoute requiredRole="ADMIN">
                      <EmailTemplatesSettings />
                    </ProtectedRoute>
                  } />
                  <Route path="settings/coupons" element={
                    <ProtectedRoute requiredRole="EDITOR">
                      <CouponsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="settings/coupons/:couponId" element={
                    <ProtectedRoute requiredRole="EDITOR">
                      <CouponDetailPage />
                    </ProtectedRoute>
                  } />
                </Route>
                
                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </DomainScope>
              </div>
            </Router>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
